<div class="care-queue-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>WhatsApp Customer Care Queue</h1>
        <p>Bot conversations handed off to a human — jump straight into WhatsApp to reply</p>
      </div>
    </div>
  </div>

  <!-- Filter Section -->
  <div class="filter-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Queue</h3>
      <button class="clear-filters-btn" (click)="clearFilters()" [disabled]="!hasActiveFilters()">
        <i class="fas fa-times"></i> Clear Filters
      </button>
    </div>

    <div class="filter-form">
      <div class="filter-row">
        <div class="filter-group">
          <label for="phoneNumberFilter">Phone Number</label>
          <input type="text" id="phoneNumberFilter" [(ngModel)]="filters.phoneNumber" class="filter-input"
            placeholder="Search by phone">
        </div>

        <div class="filter-group filter-group-checkbox">
          <label for="includeAttendedFilter">&nbsp;</label>
          <label class="checkbox-label" for="includeAttendedFilter">
            <input type="checkbox" id="includeAttendedFilter" [(ngModel)]="filters.includeAttended">
            Include attended
          </label>
        </div>

        <div class="filter-actions" style="margin-top: auto;">
          <button class="apply-filters-btn" (click)="applyFilters()">
            <i class="fas fa-search"></i> Apply Filters
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div class="error-message" *ngIf="errorMessage">
    <i class="fas fa-exclamation-circle"></i> {{ errorMessage }}
  </div>

  <!-- Success Message -->
  <div class="success-message" *ngIf="successMessage">
    <i class="fas fa-check-circle"></i> {{ successMessage }}
  </div>

  <!-- Loading State -->
  <div class="loading" *ngIf="loading && !queueResponse">
    <p><i class="fas fa-spinner fa-spin"></i> Loading care queue...</p>
  </div>

  <!-- Queue Table Wrapper -->
  <div class="table-container" *ngIf="queueResponse">
    <!-- Pagination Summary -->
    <div class="pagination-summary">
      <p>Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount || 0) }} of {{
        totalCount || 0 }} handed-off conversations
        <span class="live-indicator"><i class="fas fa-circle"></i> auto-refreshing every 30s</span>
      </p>
    </div>

    <!-- Desktop Table View -->
    <div class="desktop-table-view">
      <table class="queue-table">
        <thead>
          <tr>
            <th class="col-phone">Phone Number</th>
            <th class="col-language">Language</th>
            <th class="col-reason">Handoff Reason</th>
            <th class="col-time">Handed Off</th>
            <th class="col-status">Status</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let item of queueResponse.items || []" class="queue-row">
            <td class="col-phone">
              <div class="phone-number">{{ item.phoneNumber }}</div>
            </td>
            <td class="col-language">{{ getLanguageLabel(item.language) }}</td>
            <td class="col-reason">{{ getHandoffReasonLabel(item.handoffReason) }}</td>
            <td class="col-time">
              <span [title]="item.handoffAt">{{ getTimeSince(item.handoffAt) }}</span>
            </td>
            <td class="col-status">
              <span class="status-badge" [class.status-active]="item.isAttended" [class.status-pending]="!item.isAttended">
                {{ item.isAttended ? 'Attended' : 'Unattended' }}
              </span>
            </td>
            <td class="col-actions">
              <div class="action-buttons">
                <button class="btn btn-success btn-sm" (click)="openWhatsApp(item.phoneNumber)" title="Open in WhatsApp">
                  <i class="fab fa-whatsapp"></i> WhatsApp
                </button>
                <button class="btn btn-primary btn-sm" (click)="markAttended(item)" title="Mark Attended"
                  [disabled]="item.isAttended || attendingSessionId === item.id"
                  *appHasPermission="['Marketing_Manage', 'Telemarketer_Permissions']">
                  <i class="fas fa-spinner fa-spin" *ngIf="attendingSessionId === item.id"></i>
                  <i class="fas fa-check" *ngIf="attendingSessionId !== item.id"></i>
                  {{ item.isAttended ? 'Attended' : 'Mark Attended' }}
                </button>
              </div>
            </td>
          </tr>
          <tr *ngIf="!queueResponse.items || queueResponse.items.length === 0">
            <td colspan="6" class="no-items">
              <p>No handed-off conversations found.</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div class="mobile-card-view" style="padding: 1rem;">
      <div *ngFor="let item of queueResponse.items || []"
        class="bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-4">
        <!-- Card Header -->
        <div class="flex justify-between items-start mb-3">
          <div>
            <h3 class="text-lg font-bold text-gray-900 m-0">{{ item.phoneNumber }}</h3>
            <span class="address-preview block mt-1">{{ getLanguageLabel(item.language) }}</span>
          </div>
          <span class="status-badge" [class.status-active]="item.isAttended" [class.status-pending]="!item.isAttended">
            {{ item.isAttended ? 'Attended' : 'Unattended' }}
          </span>
        </div>

        <!-- Card Body -->
        <div class="grid grid-cols-1 gap-2 mb-4">
          <div class="flex flex-col">
            <label class="text-xs font-semibold text-gray-500 uppercase">Handoff Reason</label>
            <span class="text-gray-900">{{ getHandoffReasonLabel(item.handoffReason) }}</span>
          </div>
          <div class="flex flex-col">
            <label class="text-xs font-semibold text-gray-500 uppercase">Handed Off</label>
            <span class="text-gray-900">{{ getTimeSince(item.handoffAt) }}</span>
          </div>
        </div>

        <!-- Card Actions -->
        <div class="flex gap-2 justify-end border-t pt-3 border-gray-100">
          <button class="btn btn-success btn-sm" style="flex: 1; justify-content: center;"
            (click)="openWhatsApp(item.phoneNumber)" title="Open in WhatsApp">
            <i class="fab fa-whatsapp"></i> WhatsApp
          </button>
          <button class="btn btn-primary btn-sm" style="flex: 1; justify-content: center;"
            (click)="markAttended(item)" title="Mark Attended"
            [disabled]="item.isAttended || attendingSessionId === item.id"
            *appHasPermission="['Marketing_Manage', 'Telemarketer_Permissions']">
            <i class="fas fa-spinner fa-spin" *ngIf="attendingSessionId === item.id"></i>
            <i class="fas fa-check" *ngIf="attendingSessionId !== item.id"></i>
            {{ item.isAttended ? 'Attended' : 'Mark Attended' }}
          </button>
        </div>
      </div>
      <div *ngIf="!queueResponse.items || queueResponse.items.length === 0" class="no-items p-4 text-center">
        <p>No handed-off conversations found.</p>
      </div>
    </div>
  </div>

  <!-- Pagination Component -->
  <app-pagination *ngIf="queueResponse" [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
    (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
  </app-pagination>
</div>
